import { Delete, MoreVert, Visibility } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { useState } from 'react'
import { StyledMenu } from '../../styles/PopupStyles'
import { Trans, useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import { useDeleteProjectMutation } from '../../store/redux/reducers/projectsSlice'
import { useNotification } from '../../store/redux/hooks'
import { Project } from '../../store/types'
import { StyledContainerRowCenter } from '../../styles/ContainerStyles'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import ProjectStatus from '../../store/types/enums/ProjectStatus'
import ConfirmButton from '../Button/ConfirmButton'
import { useSelector } from 'react-redux'

const ProjectActionMenu = ({ project, isInTile }: { project: Project; isInTile?: boolean }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { displayNotification } = useNotification()
  const [deleteProject] = useDeleteProjectMutation()
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)
  const isOneOfProjectLeaders =
    Array.isArray(project.leaders) && project.leaders.some(leader => leader.id === currentUser?.id)
  const isOperationalAndNotDeleted =
    project.operational && project.projectStatus !== ProjectStatus.DELETED.toUpperCase()
  const canManageProject = isOneOfProjectLeaders || isManagement

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const handleDelete = async () => {
    if (project.projectStatus === ProjectStatus.DELETED.toUpperCase()) {
      setOpenDeleteDialog(false)
      return
    }

    try {
      await deleteProject(project.id).unwrap()
      displayNotification({
        message: t('page.projects.notificationPopup.projectRemoved'),
        type: 'success',
      })
    } catch (_) {
      displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
    }
    setOpenDeleteDialog(false)
  }

  const id = project.id

  return (
    <>
      <PopupState variant="popover" disableAutoFocus={false} popupId={'projectActionMenu'} parentPopupState={null}>
        {popupState => (
          <React.Fragment>
            <IconButton
              {...bindTrigger(popupState)}
              sx={{ color: 'text.mediumEmphasis' }}
              aria-label={`${project.name}-project-action-menu`}
            >
              <MoreVert />
            </IconButton>

            <StyledMenu
              {...bindMenu(popupState)}
              slotProps={{
                paper: {
                  sx: { maxWidth: '320px !important', minWidth: '180px' },
                },
              }}
            >
              {isInTile ? (
                <StyledContainerRowCenter
                  sx={{
                    gap: 0.25,
                    py: 1,
                    px: 2,
                    flexDirection: 'column',
                  }}
                  aria-disabled
                >
                  <Typography variant="body2" fontWeight={700}>
                    {project.name}
                  </Typography>
                  <Typography variant="body2">{project.acronym}</Typography>
                </StyledContainerRowCenter>
              ) : (
                <StyledContainerRowCenter
                  sx={{
                    gap: 1,
                    py: 1,
                    px: 2,
                    textAlign: 'center',
                  }}
                  aria-disabled
                >
                  <Tooltip title={project.name}>
                    <Typography textOverflow={'ellipsis'} noWrap maxWidth={100} variant="body2" fontWeight={700}>
                      {project.name}
                    </Typography>
                  </Tooltip>
                  <Typography variant="body2">•</Typography>
                  <Typography variant="body2">{project.acronym}</Typography>
                </StyledContainerRowCenter>
              )}

              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={() => navigate(generatePath(navigation.projectDetails, { id }))} autoFocus>
                <ListItemIcon sx={{ svg: { color: 'text.mediumEmphasis' } }}>
                  <Visibility />
                </ListItemIcon>
                <ListItemText>{t('page.projects.actionPopup.details')}</ListItemText>
              </MenuItem>

              {isOperationalAndNotDeleted && canManageProject && (
                <MenuItem onClick={() => setOpenDeleteDialog(true)}>
                  <ListItemIcon sx={{ svg: { color: 'text.mediumEmphasis' } }}>
                    <Delete />
                  </ListItemIcon>
                  <ListItemText>{t('page.projects.actionPopup.delete')}</ListItemText>
                </MenuItem>
              )}
            </StyledMenu>

            <Dialog
              onClose={() => {
                setOpenDeleteDialog(false)
              }}
              open={openDeleteDialog}
              PaperProps={{
                style: {
                  paddingLeft: theme.spacing(1),
                  paddingRight: theme.spacing(2),
                  paddingTop: theme.spacing(1),
                  paddingBottom: theme.spacing(2),
                },
              }}
            >
              <DialogTitle>{t('page.projects.dialog.delete.title')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <Trans i18nKey="page.projects.dialog.delete.content" values={{ name: project.name }} />
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ gap: 2 }}>
                <Button size="small" onClick={() => setOpenDeleteDialog(false)}>
                  {t('page.adminsPage.dialog.return')}
                </Button>
                <ConfirmButton text={t('page.projects.dialog.delete.confirm')} onClick={handleDelete} />
              </DialogActions>
            </Dialog>
          </React.Fragment>
        )}
      </PopupState>
    </>
  )
}

export default ProjectActionMenu
